import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Legend, PieChart, Pie, Cell
} from "recharts";
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from "moment";
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { useAuth } from '../Auth/AuthContext';

const RevenueChart = () => {
    const [analytics, setAnalytics] = useState([]);
    const [graphType, setGraphType] = useState("Today");
    const [activeBars, setActiveBars] = useState({
        adrevenue: true,
        adimpression: true,
        adrequest: true,
        ecpm: true,
    });

    const Auth = useAuth();
    const user = Auth.authState?.user;
    const role = Auth.authState?.roles;
    const admin = role === "Admin";

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                const response = await AnalyticsApiService.GetAnalyticsForGraph(admin ? null : user?.id, graphType);
                setAnalytics(response.data || []);
            } catch (error) {
                toast.error("Failed to fetch analytics.");
            }
        };

        if (user && user.id) {
            fetchAnalytics();
        }
    }, [user, graphType]);

    // Prepare chart data
    const chartData = analytics.map((t) => ({
        date: moment(t.date).format("MMM DD"),
        adrevenue: t.adExchangeRevenue.toFixed(2),
        adimpression: t.adExchangeImpressions,
        adrequest: t.adExchangeAdRequests,
        ecpm: t.adExchangeAvgECPM.toFixed(2),
        country: t.country,
    }));

    // Count occurrences of each country
    const countryCounts = chartData.reduce((acc, curr) => {
        if (curr.country) {
            acc[curr.country] = (acc[curr.country] || 0) + 1;
        }
        return acc;
    }, {});

    // Convert to array for Recharts
    const countryChartData = Object.keys(countryCounts).map((country) => ({
        country,
        count: countryCounts[country],
    }));

    const handleGraphTypeChange = (event) => {
        setGraphType(event.target.value);
    };

    const handleLegendClick = (event) => {
        const dataKey = event.dataKey;
        setActiveBars((prev) => ({
            ...prev,
            [dataKey]: !prev[dataKey],
        }));
    };

    const resetFilters = () => {
        setActiveBars({
            adrevenue: true,
            adimpression: true,
            adrequest: true,
            ecpm: true,
        });
    };

    return (
        <div className="p-5 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">📊 Revenue & Ad Performance Overview</h2>
            <div className="flex gap-4 mb-4">
                <select
                    name="graphType"
                    value={graphType}
                    onChange={handleGraphTypeChange}
                    className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 text-sm md:text-base transition duration-200 ease-in-out hover:bg-purple-600"
                >
                    <option value="Today">📅 Today</option>
                    <option value="Yesterday">📆 Yesterday</option>
                    <option value="LastSevenDays">📊 Last Seven Days</option>
                    <option value="ThisMonth">📈 This Month</option>
                    <option value="LastMonth">📉 Last Month</option>
                </select>

                <button
                    onClick={resetFilters}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-200"
                >
                    🔄 Reset
                </button>
            </div>

            <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                    <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#1E3A8A" />
                            <stop offset="100%" stopColor="#3B82F6" />
                        </linearGradient>
                        <linearGradient id="impressionGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#D97706" />
                            <stop offset="100%" stopColor="#FACC15" />
                        </linearGradient>
                        <linearGradient id="requestGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#065F46" />
                            <stop offset="100%" stopColor="#10B981" />
                        </linearGradient>
                        <linearGradient id="ecpmGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#6D28D9" />
                            <stop offset="100%" stopColor="#A78BFA" />
                        </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fill: "#1f2937", fontSize: 12 }} angle={-20} dy={10} />
                    <YAxis yAxisId="left" tickFormatter={(value) => `$${value}`} />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend verticalAlign="top" align="right" onClick={handleLegendClick} />

                    {activeBars.adrevenue && <Bar dataKey="adrevenue" name="Revenue" fill="url(#revenueGradient)" yAxisId="left" />}
                    {activeBars.adimpression && <Bar dataKey="adimpression" name="Ad Impressions" fill="url(#impressionGradient)" yAxisId="right" />}
                    {activeBars.adrequest && <Bar dataKey="adrequest" name="Ad Requests" fill="url(#requestGradient)" yAxisId="right" />}
                    {activeBars.ecpm && <Bar dataKey="ecpm" name="Avg eCPM" fill="url(#ecpmGradient)" yAxisId="left" />}
                </BarChart>
            </ResponsiveContainer>

            <div className="mt-5 p-5 bg-white rounded-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">🌍 Country Distribution</h3>
                <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                        {countryChartData.map((entry, index) => (
                            <defs key={index}>
                                <linearGradient id={`pieGradient${index}`} x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="#3B82F6" />
                                    <stop offset="100%" stopColor="#9333EA" />
                                </linearGradient>
                            </defs>
                        ))}

                        <Pie data={countryChartData} dataKey="count" nameKey="country" cx="50%" cy="50%" innerRadius={70} outerRadius={110} label>
                            {countryChartData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={`url(#pieGradient${index})`} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default RevenueChart;
